/**
 * Product reviews drawer — right-side panel matching filters / cart drawer conventions.
 * Opens Judge.me revamp review widget from the PDP star badge.
 */
if (!customElements.get('product-reviews-modal')) {
  customElements.define(
    'product-reviews-modal',
    class ProductReviewsModal extends HTMLElement {
      constructor() {
        super();
        this.onKeyup = this.onKeyup.bind(this);
        this.onModalClick = this.onModalClick.bind(this);
        this.eventsBound = false;
        this.activeTrigger = null;
        this.badgeSetupAttempts = 0;
        this.widgetLoadPromise = null;
        this.revampScriptPromise = null;
      }

      connectedCallback() {
        if (!this.moved) {
          this.moved = true;
          document.body.appendChild(this);
          return;
        }

        this.bindEvents();
        this.decorateTriggers();
        this.waitForJudgeMeBadge();
      }

      disconnectedCallback() {
        if (document.body.contains(this)) return;
        this.unbindEvents();
        this.clearBadgeSetupTimer();
      }

      get productId() {
        return this.dataset.productId;
      }

      get productTitle() {
        return this.dataset.productTitle || '';
      }

      get productHandle() {
        return this.dataset.productHandle || '';
      }

      get widgetMount() {
        return this.querySelector('[data-reviews-widget-mount]');
      }

      get overlay() {
        return this.querySelector('.product-reviews-modal__overlay');
      }

      get panel() {
        return this.querySelector('.product-reviews-modal__panel');
      }

      get reviewWidget() {
        return this.querySelector('.jdgm-review-widget[data-entry-point]');
      }

      get isOpen() {
        return this.classList.contains('is-open');
      }

      bindEvents() {
        if (this.eventsBound) return;
        this.addEventListener('click', this.onModalClick);
        document.addEventListener('keyup', this.onKeyup);
        this.eventsBound = true;
      }

      unbindEvents() {
        if (!this.eventsBound) return;
        this.removeEventListener('click', this.onModalClick);
        document.removeEventListener('keyup', this.onKeyup);
        this.eventsBound = false;
      }

      getTriggerRoots() {
        return document.querySelectorAll(
          `.product-template__rating[data-reviews-modal-trigger][data-product-id="${this.productId}"]`
        );
      }

      decorateTriggers() {
        this.getTriggerRoots().forEach(root => {
          root.setAttribute('role', 'button');
          root.setAttribute('tabindex', '0');
          root.setAttribute('aria-haspopup', 'dialog');
          root.setAttribute('aria-controls', this.id);
          root.setAttribute('aria-expanded', 'false');

          if (!root.getAttribute('aria-label')) {
            const heading = this.querySelector('.product-reviews-modal__title');
            root.setAttribute(
              'aria-label',
              heading?.textContent?.trim() || 'Reviews'
            );
          }

          if (root.dataset.reviewsModalBound === 'true') return;
          root.dataset.reviewsModalBound = 'true';

          root.addEventListener(
            'click',
            event => {
              event.preventDefault();
              event.stopPropagation();
              this.open(root);
            },
            { capture: true }
          );

          root.addEventListener('keydown', event => {
            if (event.key !== 'Enter' && event.key !== ' ') return;
            if (event.target !== root) return;
            event.preventDefault();
            this.open(root);
          });
        });
      }

      clearBadgeSetupTimer() {
        if (this.badgeSetupTimer) {
          window.clearTimeout(this.badgeSetupTimer);
          this.badgeSetupTimer = null;
        }
      }

      waitForJudgeMeBadge() {
        this.clearBadgeSetupTimer();

        const roots = [...this.getTriggerRoots()];
        const hasBadge = roots.some(root => root.querySelector('.jdgm-prev-badge'));

        if (hasBadge) {
          roots.forEach(root => this.setupBadgeTextFormatting(root));
          return;
        }

        if (this.badgeSetupAttempts >= 20) return;

        this.badgeSetupAttempts += 1;
        this.badgeSetupTimer = window.setTimeout(
          () => this.waitForJudgeMeBadge(),
          150
        );
      }

      setupBadgeTextFormatting(root) {
        if (root.dataset.badgeTextObserver === 'true') {
          this.formatBadgeText(root);
          return;
        }

        root.dataset.badgeTextObserver = 'true';
        this.formatBadgeText(root);

        const badge = root.querySelector('.jdgm-prev-badge');
        const textEl = root.querySelector('.jdgm-prev-badge__text');
        if (!badge || !textEl || typeof MutationObserver !== 'function') return;

        this.badgeTextObserver = this.badgeTextObserver || new MutationObserver(mutations => {
          mutations.forEach(mutation => {
            const targetRoot = mutation.target.closest(
              '.product-template__rating[data-reviews-modal-trigger]'
            );
            if (targetRoot) this.formatBadgeText(targetRoot);
          });
        });

        this.badgeTextObserver.observe(badge, {
          attributes: true,
          attributeFilter: ['data-average-rating', 'data-number-of-reviews'],
          subtree: true,
          characterData: true,
          childList: true,
        });
      }

      getReviewCount(badge, textEl) {
        const datasetCount = parseInt(badge.dataset.numberOfReviews || '', 10);
        if (!Number.isNaN(datasetCount) && datasetCount > 0) return datasetCount;

        const textCount = parseInt(
          (textEl.textContent || '').replace(/[^\d]/g, ''),
          10
        );
        return Number.isNaN(textCount) ? 0 : textCount;
      }

      formatBadgeText(root) {
        const badge = root.querySelector('.jdgm-prev-badge');
        const textEl = root.querySelector('.jdgm-prev-badge__text');
        if (!badge || !textEl) return;

        const average = parseFloat(badge.dataset.averageRating || '0');
        const count = this.getReviewCount(badge, textEl);
        let nextText;

        if (count > 0) {
          const template =
            count === 1 ? root.dataset.reviewCountOne : root.dataset.reviewCountOther;
          nextText = template
            ? template.replace('[count]', count)
            : `(${count} reviews)`;
        } else {
          nextText = root.dataset.noReviewsLabel || 'No reviews';
        }

        if (textEl.textContent === nextText) {
          badge.dataset.formattedKey = `${average}-${count}`;
          return;
        }

        textEl.textContent = nextText;
        badge.dataset.formattedKey = `${average}-${count}`;
      }

      onModalClick(event) {
        const closeTrigger = event.target.closest('[data-reviews-modal-close]');
        if (!closeTrigger || !this.contains(closeTrigger)) return;

        event.preventDefault();
        this.close();
      }

      onKeyup(event) {
        if (event.code !== 'Escape') return;
        if (!this.isOpen) return;
        this.close();
      }

      open(trigger) {
        if (this.isOpen) return;

        this.activeTrigger = trigger || null;
        this.classList.remove('is-closing');
        this.setAttribute('aria-hidden', 'false');
        this.classList.add('is-open');

        if (this.overlay) {
          this.overlay.setAttribute('aria-hidden', 'false');
          this.overlay.classList.add('is-showing');
        }

        if (this.activeTrigger) {
          this.activeTrigger.setAttribute('aria-expanded', 'true');
        }

        document.body.classList.add('overflow-hidden');

        if (typeof trapFocus === 'function') {
          const focusTarget =
            this.querySelector('[data-reviews-modal-close]') || this.panel || this;
          trapFocus(this, focusTarget);
        }

        this.loadReviewWidget();
      }

      close() {
        if (!this.isOpen || this.classList.contains('is-closing')) return;

        const panel = this.panel;

        const finishClose = () => {
          this.setAttribute('aria-hidden', 'true');
          this.classList.remove('is-open', 'is-closing');

          if (this.overlay) {
            this.overlay.setAttribute('aria-hidden', 'true');
            this.overlay.classList.remove('is-showing');
          }

          if (this.activeTrigger) {
            this.activeTrigger.setAttribute('aria-expanded', 'false');
          }

          document.body.classList.remove('overflow-hidden');

          if (typeof removeTrapFocus === 'function') {
            removeTrapFocus(this.activeTrigger || null);
          }

          this.activeTrigger = null;
        };

        this.classList.add('is-closing');

        requestAnimationFrame(() => {
          this.classList.remove('is-open');

          if (this.overlay) {
            this.overlay.classList.remove('is-showing');
          }

          const prefersReducedMotion =
            typeof window.matchMedia === 'function' &&
            window.matchMedia('(prefers-reduced-motion: reduce)').matches;

          const hasTransition = (() => {
            if (!panel || prefersReducedMotion) return false;
            const computed = getComputedStyle(panel);
            return computed.transitionDuration
              .split(',')
              .some(value => parseFloat(value) > 0);
          })();

          if (hasTransition) {
            panel.addEventListener('transitionend', finishClose, { once: true });
          } else {
            finishClose();
          }
        });
      }

      widgetHasContent(widget) {
        if (!widget) return false;

        return (
          widget.querySelector('.jm-review-widget, .jdgm-rev, .jdgm-rev-widg') !== null ||
          widget.textContent.trim().length > 80
        );
      }

      getJudgeMe() {
        return typeof window.jdgm === 'object' && window.jdgm !== null ? window.jdgm : null;
      }

      getShopDomain(jdgm) {
        if (typeof jdgm.shopDomain === 'function') {
          return jdgm.shopDomain();
        }

        return window.jdgmSettings?.shop_domain || window.Shopify?.shop || '';
      }

      reviewDataHasReviews(data) {
        if (!data) return false;
        return (
          (Array.isArray(data.reviews) && data.reviews.length > 0) ||
          (typeof data.number_of_reviews === 'number' && data.number_of_reviews > 0) ||
          (typeof data.review_count === 'number' && data.review_count > 0)
        );
      }

      ensureReviewWidgetData(jdgm) {
        window.jdgm = jdgm;
        jdgm.data = jdgm.data || {};
        jdgm.data.reviewWidget = jdgm.data.reviewWidget || {};

        const existingData = jdgm.data.reviewWidget[this.productId];
        if (this.reviewDataHasReviews(existingData)) {
          return Promise.resolve(existingData);
        }

        const shopDomain = this.getShopDomain(jdgm);
        const settings = window.jdgmSettings || {};
        const params = new URLSearchParams({
          url: shopDomain,
          shop_domain: shopDomain,
          platform: settings.platform || 'shopify',
          product_id: this.productId,
          page: '1',
          per_page: String(settings.product_review_widget_per_page || 10),
        });

        return fetch(`${jdgm.API_HOST}/reviews/reviews_for_widget?${params.toString()}`)
          .then(response => (response.ok ? response.json() : null))
          .then(data => {
            if (data) {
              jdgm.data.reviewWidget[this.productId] = data;
            }
            return data;
          })
          .catch(() => null);
      }

      loadStylesheet(href) {
        if (document.querySelector(`link[href="${href}"]`)) return;
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = href;
        document.head.appendChild(link);
      }

      loadStylesFromManifest(cdnBase, manifest, entryKey) {
        const visited = new Set();

        const loadEntryStyles = key => {
          if (!key || visited.has(key)) return;
          visited.add(key);

          const entry = manifest[key];
          if (!entry) return;

          entry.css?.forEach(cssPath => {
            this.loadStylesheet(`${cdnBase}${cssPath}`);
          });

          entry.imports?.forEach(loadEntryStyles);
        };

        loadEntryStyles(entryKey);
      }

      loadRevampWidgetScript(jdgm) {
        if (this.revampScriptPromise) {
          return this.revampScriptPromise;
        }

        const cdnBase = jdgm.CDN_BASE_URL;
        const entryKey = 'review-widget/main.js';
        const scriptFile = 'review_widget.js';

        if (!cdnBase) {
          return Promise.resolve();
        }

        this.revampScriptPromise = fetch(`${cdnBase}manifest.json?v=${Date.now()}`)
          .then(response => (response.ok ? response.json() : {}))
          .catch(() => ({}))
          .then(manifest => {
            this.loadStylesFromManifest(cdnBase, manifest, entryKey);

            return new Promise(resolve => {
              const script = document.createElement('script');
              script.type = 'module';
              script.src = `${cdnBase}${scriptFile}?v=${Date.now()}`;
              script.onload = resolve;
              script.onerror = resolve;
              document.head.appendChild(script);
            });
          });

        return this.revampScriptPromise;
      }

      createReviewWidgetElement() {
        const widget = document.createElement('div');
        widget.id = 'judgeme_product_reviews';
        widget.className = 'jdgm-widget jdgm-review-widget';
        widget.dataset.widget = 'review';
        widget.dataset.entryPoint = 'review_widget.js';
        widget.dataset.entryKey = 'review-widget/main.js';
        widget.dataset.productId = this.productId;
        widget.dataset.productTitle = this.productTitle;
        widget.dataset.productHandle = this.productHandle;
        return widget;
      }

      applyWidgetBrandStyles(widget) {
        if (!widget) return;

        const brandVars = {
          '--jm-review-widget-star-and-histogram-color': '#232220',
          '--jm-review-widget-button-color': '#232220',
          '--jm-review-widget-button-text-color': '#ffffff',
          '--jm-review-widget-text-color': '#232220',
          '--jm-review-widget-lighter-text-color': 'rgba(35, 34, 32, 0.55)',
          '--jm-widget-primary-color': '#232220',
          '--jm-review-widget-form-button-color': '#232220',
          '--jm-review-widget-form-button-text-color': '#ffffff',
          '--jm-review-widget-form-text-color': '#232220',
        };

        Object.entries(brandVars).forEach(([name, value]) => {
          widget.style.setProperty(name, value);
        });

        widget
          .querySelectorAll('.jm-star-rating')
          .forEach(ratingEl => {
            Object.entries(brandVars).forEach(([name, value]) => {
              ratingEl.style.setProperty(name, value);
            });
          });
      }

      decorateStarRatings(container) {
        if (!container) return;

        container.querySelectorAll('.jm-star-rating[aria-label]').forEach(ratingEl => {
          const label = ratingEl.getAttribute('aria-label') || '';
          const match = label.match(/([\d.]+)\s+out of\s+5/i);
          if (!match) return;

          const rating = parseFloat(match[1]);
          if (Number.isNaN(rating)) return;

          ratingEl.querySelectorAll('.jm-star-rating__font-icon').forEach((icon, index) => {
            icon.classList.remove('is-star-filled', 'is-star-half', 'is-star-empty');

            const starNumber = index + 1;
            if (rating >= starNumber) {
              icon.classList.add('is-star-filled');
            } else if (rating >= starNumber - 0.5) {
              icon.classList.add('is-star-half');
            } else {
              icon.classList.add('is-star-empty');
            }
          });
        });
      }

      observeWidgetStars(widget) {
        if (!widget || typeof MutationObserver !== 'function') return;

        this.decorateStarRatings(widget);
        this.applyWidgetBrandStyles(widget);

        if (this.starRatingObserver) {
          this.starRatingObserver.disconnect();
        }

        this.starRatingObserver = new MutationObserver(() => {
          const activeWidget = this.reviewWidget;
          if (!activeWidget) return;
          this.decorateStarRatings(activeWidget);
          this.applyWidgetBrandStyles(activeWidget);
        });

        this.starRatingObserver.observe(widget, {
          childList: true,
          subtree: true,
        });
      }

      waitForWidgetContent(widget, attempts = 0) {
        if (!widget) return;

        this.applyWidgetBrandStyles(widget);
        this.decorateStarRatings(widget);

        if (this.widgetHasContent(widget)) {
          this.observeWidgetStars(widget);
          return;
        }

        if (attempts >= 30) return;

        window.setTimeout(() => {
          this.waitForWidgetContent(widget, attempts + 1);
        }, 200);
      }

      mountReviewWidget(jdgm) {
        const mount = this.widgetMount;
        if (!mount) return Promise.resolve();

        const existingWidget = this.reviewWidget;
        if (existingWidget && this.widgetHasContent(existingWidget)) {
          this.observeWidgetStars(existingWidget);
          return Promise.resolve();
        }

        mount.innerHTML = '';
        const widget = this.createReviewWidgetElement();
        mount.appendChild(widget);
        this.applyWidgetBrandStyles(widget);

        return this.loadRevampWidgetScript(jdgm).then(() => {
          this.waitForWidgetContent(widget);
        });
      }

      loadReviewWidget() {
        const jdgm = this.getJudgeMe();
        if (!jdgm) return;

        if (this.widgetLoadPromise) {
          return this.widgetLoadPromise;
        }

        this.widgetLoadPromise = this.ensureReviewWidgetData(jdgm)
          .then(() => this.mountReviewWidget(jdgm))
          .finally(() => {
            this.widgetLoadPromise = null;
          });

        return this.widgetLoadPromise;
      }
    }
  );
}
